import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogOverlay,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Bug,
  Camera,
  CheckCircle,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  Edit3,
  Image as ImageIcon
} from 'lucide-react';
import bugReportCapture from '@/services/bugReportCapture';
import ScreenshotAnnotator from './ScreenshotAnnotator';

const API = process.env.REACT_APP_BACKEND_URL || '';

const BugReportModal = ({ open, onOpenChange, preCapturedData }) => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedReference, setSubmittedReference] = useState(null);
  const [submittedSupportReference, setSubmittedSupportReference] = useState(null);
  const [error, setError] = useState('');
  const [duplicate, setDuplicate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showAnnotator, setShowAnnotator] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'ui_bug',
    priority: 'medium'
  });
  
  const [capturedData, setCapturedData] = useState(null);
  const [capturing, setCapturing] = useState(false);

  // Use pre-captured data if provided, otherwise capture when modal opens
  useEffect(() => {
    if (open) {
      if (preCapturedData) {
        setCapturedData(preCapturedData);
      } else if (!capturedData) {
        handleCapture();
      }
    }
  }, [open, preCapturedData]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setTimeout(() => {
        setFormData({
          title: '',
          description: '',
          category: 'ui_bug',
          priority: 'medium'
        });
        setCapturedData(null);
        setSubmitted(false);
        setSubmittedReference(null);
        setSubmittedSupportReference(null);
        setError('');
        setDuplicate(null);
        setShowPreview(false);
        setShowAnnotator(false);
      }, 300);
    }
  }, [open]);

  const handleCapture = async () => {
    setCapturing(true);
    try {
      const data = await bugReportCapture.compileReportData();
      setCapturedData(data);
    } catch (err) {
      console.error('Failed to capture data:', err);
      setError('Failed to capture technical data');
    } finally {
      setCapturing(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAnnotationSave = (annotatedScreenshot) => {
    setCapturedData(prev => ({
      ...prev,
      screenshot_data: annotatedScreenshot
    }));
    setShowAnnotator(false);
  };

  const checkDuplicate = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return false;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/api/bug-reports/check-duplicate`,
        {
          title: formData.title,
          url: window.location.pathname
        },
        { headers }
      );

      if (response.data.is_duplicate) {
        setDuplicate(response.data.existing_report);
        return true;
      }
      
      return false;
    } catch (err) {
      console.error('Failed to check duplicate:', err);
      return false;
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Please provide a title');
      return;
    }

    if (!formData.description.trim()) {
      setError('Please describe the issue');
      return;
    }

    if (!capturedData) {
      setError('Technical data not captured. Please try again.');
      return;
    }

    // Check for duplicate
    const isDuplicate = await checkDuplicate();
    if (isDuplicate && !duplicate) {
      // If duplicate found but user hasn't been warned yet, show warning
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const headers = { Authorization: `Bearer ${token}` };

      // Prepare bug report payload
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        priority: formData.priority,
        context: {
          user_steps: capturedData.user_steps,
          current_page: capturedData.current_page,
          environment: capturedData.environment,
          console_errors: capturedData.console_errors,
          api_calls: capturedData.api_calls,
          captured_at: capturedData.captured_at
        },
        screenshot_data: capturedData.screenshot_data
      };

      const response = await axios.post(`${API}/api/bug-reports`, payload, { headers });
      
      // Store the reference number to show in success message
      setSubmittedReference(response.data.reference);
      setSubmittedSupportReference(response.data.support_reference || null);
      setSubmitted(true);
      
      // Auto-close after 3 seconds
      setTimeout(() => {
        onOpenChange(false);
      }, 3000);

    } catch (err) {
      console.error('Failed to submit bug report:', err);
      const errorDetail = err.response?.data?.detail;
      let errorMessage = 'Failed to submit bug report';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (Array.isArray(errorDetail)) {
        errorMessage = errorDetail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (errorDetail && typeof errorDetail === 'object') {
        errorMessage = JSON.stringify(errorDetail);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogOverlay data-bug-report-overlay="true" />
        <DialogContent className="sm:max-w-md" data-bug-report-modal="true">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="h-16 w-16 text-green-600 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Bug Report Submitted!</h3>
            {submittedSupportReference && (
              <p className="text-lg font-mono font-bold text-primary mb-1">
                {submittedSupportReference}
              </p>
            )}
            {submittedReference && (
              <p className="text-sm font-mono text-muted-foreground mb-2">
                Bug report {submittedReference}
              </p>
            )}
            <p className="text-sm text-gray-600">
              Thank you for reporting this issue. Our team will review it shortly.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // Show annotation editor
  if (showAnnotator && capturedData?.screenshot_data) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogOverlay data-bug-report-overlay="true" />
        <DialogContent className="sm:max-w-3xl max-h-[90vh]" data-bug-report-modal="true">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit3 className="h-5 w-5 text-blue-600" />
              Mark the Issue
            </DialogTitle>
            <DialogDescription>
              Use the tools to highlight the problem area on the screenshot
            </DialogDescription>
          </DialogHeader>
          
          <ScreenshotAnnotator
            screenshotData={capturedData.screenshot_data}
            onSave={handleAnnotationSave}
            onCancel={() => setShowAnnotator(false)}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogOverlay data-bug-report-overlay="true" />
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-hidden" data-bug-report-modal="true">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bug className="h-5 w-5 text-red-600" />
            Report a Bug
          </DialogTitle>
          <DialogDescription>
            Help us improve by reporting any issues you encounter
          </DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto max-h-[calc(90vh-180px)] pr-2">
          <div className="space-y-4 pb-4">
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* Duplicate Warning */}
            {duplicate && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You reported a similar issue on{' '}
                  {new Date(duplicate.reported_at).toLocaleString()}.
                  You can still submit if this is a different problem.
                </AlertDescription>
              </Alert>
            )}

            {/* Capture Status */}
            {capturing && (
              <Alert>
                <Loader2 className="h-4 w-4 animate-spin" />
                <AlertDescription>Capturing page data...</AlertDescription>
              </Alert>
            )}

            {capturedData && !capturing && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  Technical data captured successfully
                </AlertDescription>
              </Alert>
            )}

            {/* Title */}
            <div>
              <Label htmlFor="title">
                Bug Title <span className="text-red-500">*</span>
              </Label>
              <Input
                id="title"
                placeholder="Brief description of the issue"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                placeholder="Describe what happened and what you expected to happen..."
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
                className="mt-1"
              />
            </div>

            {/* Category and Priority */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="category">Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => handleChange('category', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ui_bug">UI Bug</SelectItem>
                    <SelectItem value="api_error">API Error</SelectItem>
                    <SelectItem value="performance">Performance</SelectItem>
                    <SelectItem value="feature_request">Feature Request</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="priority">Priority</Label>
                <Select
                  value={formData.priority}
                  onValueChange={(value) => handleChange('priority', value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Preview Button */}
            {capturedData && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPreview(!showPreview)}
                className="w-full"
              >
                {showPreview ? (
                  <>
                    <EyeOff className="h-4 w-4 mr-2" />
                    Hide Technical Preview
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    Show Technical Preview
                  </>
                )}
              </Button>
            )}

            {/* Technical Data Preview */}
            {showPreview && capturedData && (
              <Card>
                <CardContent className="pt-6">
                  <div className="space-y-4 text-sm">
                    {/* User Steps */}
                    <div>
                      <h4 className="font-semibold mb-2">Recent Actions</h4>
                      <ul className="space-y-1 text-gray-600">
                        {capturedData.user_steps.map((step, index) => (
                          <li key={index} className="text-xs">{step}</li>
                        ))}
                      </ul>
                    </div>

                    {/* Browser Info */}
                    <div>
                      <h4 className="font-semibold mb-2">Environment</h4>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="secondary">{capturedData.environment.browser}</Badge>
                        <Badge variant="secondary">{capturedData.environment.os}</Badge>
                        <Badge variant="secondary">{capturedData.environment.device}</Badge>
                        <Badge variant="secondary">{capturedData.environment.screen}</Badge>
                      </div>
                    </div>

                    {/* Console Errors */}
                    {capturedData.console_errors.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Recent Errors ({capturedData.console_errors.length})</h4>
                        <div className="text-xs text-gray-600 max-h-32 overflow-y-auto">
                          {capturedData.console_errors.slice(0, 3).map((error, index) => (
                            <div key={index} className="mb-2 p-2 bg-red-50 rounded">
                              <span className="font-mono">{error.message.substring(0, 150)}...</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* API Calls */}
                    {capturedData.api_calls.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-2">Recent API Calls ({capturedData.api_calls.length})</h4>
                        <div className="text-xs space-y-1">
                          {capturedData.api_calls.slice(0, 3).map((call, index) => (
                            <div key={index} className="flex items-center gap-2">
                              <Badge variant={call.status >= 400 ? 'destructive' : 'secondary'}>
                                {call.status}
                              </Badge>
                              <span className="font-mono">{call.method} {call.url.substring(0, 50)}...</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Screenshot Preview & Annotate */}
            {capturedData?.screenshot_data && (
              <div>
                <Label className="mb-2 block">Screenshot</Label>
                <Card className="p-2">
                  <img 
                    src={capturedData.screenshot_data} 
                    alt="Captured screenshot" 
                    className="w-full rounded border max-h-48 object-contain bg-gray-50"
                  />
                </Card>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAnnotator(true)}
                  className="w-full mt-2"
                >
                  <Edit3 className="h-4 w-4 mr-2" />
                  Mark the Issue on Screenshot
                </Button>
              </div>
            )}

            {/* Recapture Button */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleCapture}
              disabled={capturing}
              className="w-full"
            >
              {capturing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Capturing...
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  {capturedData ? 'Recapture Screenshot & Data' : 'Capture Screenshot & Data'}
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-2 pt-4 border-t">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={loading || capturing || !capturedData}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Bug className="h-4 w-4 mr-2" />
                Submit Report
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BugReportModal;
